import { useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@devhop/ui';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import type { CreatePromotionRecordDTO } from '@/modules/hr/promotion/domain/types';
import {
    useCreatePromotionRecordMutation,
    usePromotionLevelsLookupQuery,
} from '../api/queries';
import { PromotionRecordForm } from '../ui/PromotionRecordForm';
import {
    PromotionTour,
    promotionRecordCreateTourSteps,
    usePromotionTour,
} from '../tour';

export function PromotionRecordCreatePage() {
    const nav = useNavigate();
    const createMutation = useCreatePromotionRecordMutation();
    const levelsQuery = usePromotionLevelsLookupQuery();
    const tour = usePromotionTour('recordCreate');

    const back = () =>
        nav({
            to: '/app/hr/promotion/records',
            search: {
                offset: 0,
                limit: 20,
                sort: undefined,
                employeeId: undefined,
                periodYear: undefined,
                periodMonth: undefined,
                positionId: undefined,
                search: undefined,
            },
        });

    const submit = (values: CreatePromotionRecordDTO) => {
        createMutation.mutate(values, {
            onSuccess: () => {
                toast.success('ບັນທຶກ promotion record ສຳເລັດ');
                back();
            },
            onError: (err) =>
                toast.error(err.message ?? 'ສ້າງ record ບໍ່ສຳເລັດ'),
        });
    };

    return (
        <>
            <Header />
            <Main>
                <PromotionTour
                    steps={promotionRecordCreateTourSteps}
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between gap-2"
                    data-tourid="promotion-record-create-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ສ້າງບັນທຶກການເລື່ອນຕຳແໜ່ງ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="h-4 w-4" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            ບັນທຶກການເລື່ອນຂັ້ນ ຫຼື ການເຕີບໂຕຂອງພະນັກງານ
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={back}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບຄືນ
                    </Button>
                </div>
                <div className="mx-auto max-w-3xl space-y-6" data-tourid="promotion-record-create-form">
                    <PromotionRecordForm
                    mode="create"
                    onSubmit={submit}
                    onCancel={back}
                    isSubmitting={createMutation.isPending}
                    levelOptions={(levelsQuery.data ?? []).map((item) => ({
                        value: item.id,
                        label: item.label,
                    }))}
                    />
                </div>
            </Main>
        </>
    );
}
